import { mountEscapeButton } from '../escape-button';
import { extractAuto, type AutoExtractResult } from '../exam-upload/ocr-client';
import { renderParentConfirm } from '../exam-upload/parent-confirm';
import { promoteApprovedItems } from '../exam-upload/promote';
import { archiveExam } from '../exam-upload/archive';
import { verifyGradedItems } from '../exam-upload/verify-grading';
import { feedMistakesToWiki } from './wiki-bridge';
import type { ExtractedItem } from '../exam-upload/ocr-client';
import { runVariantPractice } from './variant-practice';
import { loadProgress, saveProgress, createFreshProgress } from '../../shared/progress';
import { loadVocab, addVocabBatch, type VocabWord } from '../../shared/vocab-store';
import { ensureAcademicTheme } from '../ui/theme';
import { academicButton } from '../ui/components';

/**
 * 📷 統一拍照上傳(考卷 / 單字)。
 *
 * 老闆回饋:之前「拍考卷批改」跟「拍單字表」是兩個入口,使用者要先想清楚這張是
 * 哪種才能選對按鈕。改成一個入口:一次多選照片,系統自動判斷每張是考卷還是單字
 * 表,分別處理。
 *
 * 流程:
 *   <input type=file multiple accept=image/*>(不設 capture,可從相簿一次多選)
 *   → 逐張 extractAuto(顯示「辨識中 N/M」),依 type 分流彙整
 *   → 摘要:「辨識到 X 張單字表(共 N 個單字)、Y 張考卷(共 M 題)」
 *   → 單字 → addVocabBatch 進單字庫;考卷 → renderParentConfirm → promote(逐題確認 → 錯題)
 *   → 若兩種都有,先處理單字、再帶到考卷確認
 *
 * 視覺 = 學術風格設計系統(boss 2026-06-08;見 wiki page-470f59c0)。整頁套紙底
 * 頁殼(`.ef-page` + serif 標題列),沿用本畫面自管的 escape 生命週期(故手動組
 * 頁殼,不用 pageShell 的 onBack,避免多掛一顆 ✕,作法同 exam-bank/practice)。
 * 檔案選擇用紙片 field、返回 / 完成鈕用 `academicButton`(secondary)。
 *
 * 學習介面要專業(boss 2026-05-23):無音效、無 mascot、無彩光、文案中性。
 * 學習/遊戲分離鐵則:只動單字 / 弱點 / 錯題本資料,絕不碰 commanderXp / 戰鬥數值。
 * 多選保留(不設 capture)、OCR 自動分類、錯題回流、所有 data-testid 不變。
 */
export function runPhotoUpload(container: HTMLElement, onDone: () => void): void {
  ensureAcademicTheme();
  ensurePhotoUploadStyle();

  const unmountEscape = mountEscapeButton(onDone);

  container.innerHTML = '';

  // 紙底頁殼(手動組:本畫面自管 escape,不走 pageShell 的 onBack)。
  const page = document.createElement('div');
  page.className = 'ef-page';
  const inner = document.createElement('div');
  inner.className = 'ef-page__inner';
  page.appendChild(inner);

  const header = document.createElement('header');
  header.className = 'ef-page__header';
  const heading = document.createElement('div');
  heading.className = 'ef-page__heading';
  const h1 = document.createElement('h1');
  h1.className = 'ef-title photo-upload__title';
  h1.textContent = '拍照上傳(考卷 / 單字)';
  const sub = document.createElement('p');
  sub.className = 'ef-subtitle photo-upload__help';
  sub.textContent = '選擇一張或多張(完成的)照片,系統自動判斷考卷或單字表:單字加入單字庫;考卷會自動批改、判好對錯,你只要看一眼確認即可。';
  heading.append(h1, sub);
  header.appendChild(heading);
  inner.appendChild(header);

  const root = document.createElement('div');
  root.className = 'photo-upload ef-page__body';
  root.setAttribute('data-testid', 'photo-upload-screen');
  inner.appendChild(root);

  const field = document.createElement('label');
  field.className = 'photo-upload__field';
  const fieldLabel = document.createElement('span');
  fieldLabel.className = 'photo-upload__field-label';
  fieldLabel.textContent = '選擇照片(可從相簿多選)';
  field.appendChild(fieldLabel);

  const fileInput = document.createElement('input');
  fileInput.type = 'file';
  fileInput.accept = 'image/*';
  fileInput.multiple = true;
  // 不要設 capture:iOS 上 capture 會強制開相機、只能拍一張、無法從相簿選/多選。
  fileInput.className = 'photo-upload__file';
  fileInput.setAttribute('data-testid', 'photo-upload-file');
  field.appendChild(fileInput);
  root.appendChild(field);

  const status = document.createElement('p');
  status.className = 'photo-upload__status';
  status.setAttribute('data-testid', 'photo-upload-status');
  root.appendChild(status);

  // 考卷確認 / 結果掛載區(parent-confirm 直接 append 到這裡)。
  const stage = document.createElement('div');
  stage.className = 'photo-upload__stage';
  stage.setAttribute('data-testid', 'photo-upload-stage');
  root.appendChild(stage);

  const backBtn = academicButton({
    label: '返回',
    variant: 'secondary',
    onClick: () => {
      unmountEscape();
      onDone();
    },
  });
  backBtn.className = 'ef-btn ef-btn--secondary photo-upload__back';
  backBtn.setAttribute('data-testid', 'photo-upload-back');
  root.appendChild(backBtn);

  container.appendChild(page);

  fileInput.addEventListener('change', async () => {
    const files = Array.from(fileInput.files ?? []);
    if (files.length === 0) return;

    fileInput.disabled = true;
    stage.innerHTML = '';
    status.textContent = '';

    let vocabSheets = 0;
    let examSheets = 0;
    const vocab: VocabWord[] = [];
    const examItems: ExtractedItem[] = [];

    // 進度條:OCR 是單一長呼叫(~1 分鐘)沒有中間進度,所以用「依時間平滑前進」的
    // 進度條 —— 隨秒數 ease-out 爬到 ~95%、辨識完成才補滿(boss 2026-06-10「要不然
    // 太久了」)。多張時顯示第幾張 + 已花秒數,讓等待有感。
    const total = files.length;
    const prog = createProgress();
    status.insertAdjacentElement('afterend', prog.el);
    let completed = 0;
    let phaseStart = Date.now();
    const overallStart = Date.now();
    const TAU = 26_000; // ease-out 時間常數(~70-90s 到 95%)
    prog.update(0, total > 1 ? `準備辨識 ${total} 張…` : 'AI 正在仔細讀整張考卷,約需 1 分鐘…');
    const timer = setInterval(() => {
      const elapsed = Date.now() - phaseStart;
      const segStart = completed / total;
      const segEnd = (completed + 1) / total;
      const within = Math.min(0.95, 1 - Math.exp(-elapsed / TAU));
      const frac = segStart + within * (segEnd - segStart);
      const secs = Math.round((Date.now() - overallStart) / 1000);
      const head = total > 1 ? `辨識中 第 ${completed + 1}/${total} 張` : '辨識中';
      prog.update(frac, `${head} · 已 ${secs} 秒(辨識較久,請稍候)`);
    }, 250);

    for (let i = 0; i < files.length; i++) {
      completed = i;
      phaseStart = Date.now();
      const result: AutoExtractResult = await extractAuto(files[i]);
      if (result.type === 'vocab') {
        const got = result.vocab ?? [];
        if (got.length > 0) {
          vocabSheets++;
          for (const w of got) vocab.push({ word: w.word, zh: w.zh });
        }
      } else {
        const got = result.exam ?? [];
        if (got.length > 0) {
          examSheets++;
          examItems.push(...got);
        }
      }
    }

    clearInterval(timer);
    prog.update(1, '辨識完成');

    // 🧮 圖表題驗算第二關:文字模型按結構化表格重算正解再比對,覆蓋第一關
    // 邊看圖邊批改的不穩結果(boss 2026-06-11「兩個根本不一樣」)。失敗保留原判。
    if (examItems.length > 0) {
      prog.update(1, '正在驗算圖表題的批改…');
      await verifyGradedItems(examItems, (done2, total2) => {
        prog.update(1, `正在驗算圖表題的批改… ${done2} / ${total2} 題`);
      });
    }
    prog.remove();
    fileInput.disabled = false;

    // 摘要文案。
    if (vocabSheets === 0 && examSheets === 0) {
      status.textContent = '沒有辨識到單字或題目,請換一張更清楚的照片再試。';
      return;
    }
    status.textContent =
      `辨識到 ${vocabSheets} 張單字表(共 ${vocab.length} 個單字)、` +
      `${examSheets} 張考卷(共 ${examItems.length} 題)。`;

    // 1) 單字 → 直接進單字庫(去重在 addVocabBatch 內處理)。
    let vocabAdded = 0;
    if (vocab.length > 0) {
      addVocabBatch([...loadVocab(), ...vocab]);
      vocabAdded = vocab.length;
    }

    // 2) 考卷 → 逐題家長確認 → promote(弱點 + 錯題本)。
    if (examItems.length > 0) {
      renderParentConfirm(stage, examItems, (inputs) => {
        const progress = loadProgress() ?? createFreshProgress('指揮官');
        const result = promoteApprovedItems(progress, inputs);
        // 整張卷歸檔(答對的也存)→ 考卷紀錄隨時可出變形卷(boss 2026-06-10
        // 「考卷沒錯的地方就沒了」)。
        archiveExam(progress, inputs);
        saveProgress(progress);
        // 錯題回流學生 LLM Wiki(R-001;best-effort,不阻斷)。
        feedMistakesToWiki(
          progress,
          inputs
            .filter((i) => i.decision === 'wrong' || i.decision === 'careless')
            .map((i) => ({
              subject: i.item.subject,
              kp: i.item.kp,
              prompt: i.item.prompt,
              correctAnswer: i.item.correctAnswer,
            })),
        );
        // 確認後保留「沒丟棄」的題目供出變形練習卷(答對的也算);帶上批改
        // 結果,重點短卷挑題時答錯/粗心優先。
        const kept = inputs
          .filter((i) => i.decision !== 'discard')
          .map((i) => ({ ...i.item, decision: i.decision as 'correct' | 'wrong' | 'careless' }));
        const correctCount = inputs.filter((i) => i.decision === 'correct').length;
        showResult(stage, vocabAdded, correctCount, result.discarded, result.mistakesAdded, kept, onDone, unmountEscape);
      });
    } else {
      // 只有單字,沒有考卷 → 直接顯示結果。
      showResult(stage, vocabAdded, 0, 0, 0, [], onDone, unmountEscape);
    }
  });
}

/**
 * 完成畫面:摘要 + 回到學習堡壘。專業介面,無彩光。
 * 摘要逐項交代「每一題去了哪裡」(boss 2026-06-10 流程重設計:答對的不再無聲蒸發)
 *   答對 → 計入熟練度;答錯/粗心 → 錯題本;整卷 → 考卷紀錄(隨時可出變形卷)。
 */
function showResult(
  stage: HTMLElement,
  vocabAdded: number,
  correctCount: number,
  discarded: number,
  mistakesAdded: number,
  examItems: ExtractedItem[],
  onDone: () => void,
  unmountEscape: () => void,
): void {
  stage.innerHTML = '';
  const summary = document.createElement('div');
  summary.className = 'photo-upload__result ef-card';
  summary.setAttribute('data-testid', 'photo-upload-result');

  const lines: string[] = [];
  if (vocabAdded > 0) lines.push(`已加入 ${vocabAdded} 個單字到單字庫。`);
  if (correctCount > 0) lines.push(`✓ 答對 ${correctCount} 題,已計入熟練度(複習排程會自動調整)。`);
  if (mistakesAdded > 0) lines.push(`✗ ${mistakesAdded} 道錯題已加入錯題本,之後用原題重練。`);
  if (discarded > 0) lines.push(`已丟棄 ${discarded} 道(誤辨識/不需要的題)。`);
  if (examItems.length > 0) {
    lines.push(`📂 整張考卷(${examItems.length} 題)已存入「考卷紀錄」,之後隨時可再出變形練習卷。`);
  }

  const title = document.createElement('h3');
  title.className = 'photo-upload__result-title';
  title.textContent = '完成';
  summary.appendChild(title);
  for (const l of lines) {
    const p = document.createElement('p');
    p.textContent = l;
    summary.appendChild(p);
  }

  // 🔁 出變形練習卷:用這張卷的題目(答對的也算)出同概念變形題。
  // boss 2026-06-10「答對的就沒管他?應該可以出變形題」。
  if (examItems.length > 0) {
    const variantBtn = academicButton({
      label: '🔁 出變形練習卷',
      variant: 'primary',
      onClick: () => {
        unmountEscape(); // variant 畫面用自己的 pageShell escape,先卸掉這層避免雙鈕
        stage.innerHTML = '';
        void runVariantPractice(stage, examItems, onDone);
      },
    });
    variantBtn.className = 'ef-btn ef-btn--primary photo-upload__variant';
    variantBtn.setAttribute('data-testid', 'photo-upload-variant');
    summary.appendChild(variantBtn);
  }

  // 有變形按鈕時「返回」退為次要;純單字(無變形)時「返回」維持主要動作。
  const doneVariant: 'primary' | 'secondary' = examItems.length > 0 ? 'secondary' : 'primary';
  const doneBtn = academicButton({
    label: '返回',
    variant: doneVariant,
    onClick: () => {
      unmountEscape();
      onDone();
    },
  });
  doneBtn.className = `ef-btn ef-btn--${doneVariant} photo-upload__done`;
  doneBtn.setAttribute('data-testid', 'photo-upload-done');
  summary.appendChild(doneBtn);

  stage.appendChild(summary);
}

/** 進度條控制把手。 */
interface ProgressHandle {
  el: HTMLElement;
  /** frac 0..1 + 文字說明。 */
  update(frac: number, text: string): void;
  remove(): void;
}

/** 建一個學術風進度條(軌道 + 填充 + 說明文字)。 */
function createProgress(): ProgressHandle {
  const el = document.createElement('div');
  el.className = 'photo-upload__progress';
  el.setAttribute('data-testid', 'photo-upload-progress');
  const bar = document.createElement('div');
  bar.className = 'photo-upload__bar';
  const fill = document.createElement('div');
  fill.className = 'photo-upload__fill';
  bar.appendChild(fill);
  const cap = document.createElement('div');
  cap.className = 'photo-upload__caption';
  el.append(bar, cap);
  return {
    el,
    update(frac: number, text: string): void {
      const pct = Math.round(Math.max(0, Math.min(1, frac)) * 100);
      fill.style.width = `${pct}%`;
      cap.textContent = text;
    },
    remove(): void {
      el.remove();
    },
  };
}

/** 拍照上傳 scoped 樣式(只用 ef- token,不碰全域 styles.css)。冪等注入。 */
const PHOTO_UPLOAD_STYLE_ID = 'ef-photo-upload-style';
function ensurePhotoUploadStyle(): void {
  if (typeof document === 'undefined') return;
  if (document.getElementById(PHOTO_UPLOAD_STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = PHOTO_UPLOAD_STYLE_ID;
  style.textContent = `
.photo-upload { display:flex; flex-direction:column; gap:16px; color:var(--ef-ink-soft); font-family:var(--ef-sans); }
.photo-upload__field {
  display:flex; flex-direction:column; gap:8px; padding:18px; background:var(--ef-paper-bright);
  border:1px dashed var(--ef-line); border-radius:var(--ef-radius); box-shadow:var(--ef-shadow-card);
}
.photo-upload__field-label { font-family:var(--ef-serif); font-weight:600; font-size:16px; color:var(--ef-ink); letter-spacing:0.5px; }
.photo-upload__file { font-family:var(--ef-sans); font-size:14px; color:var(--ef-ink-soft); }
.photo-upload__status { margin:0; font-size:14px; line-height:1.6; color:var(--ef-sub); }
.photo-upload__status:empty { display:none; }
.photo-upload__progress { display:flex; flex-direction:column; gap:8px; margin:6px 0; }
.photo-upload__bar {
  height:12px; background:var(--ef-paper); border:1px solid var(--ef-line);
  border-radius:var(--ef-radius-pill); overflow:hidden;
}
.photo-upload__fill {
  height:100%; width:0%; background:var(--ef-green); border-radius:inherit;
  transition:width 0.4s ease;
}
.photo-upload__caption { font-size:13px; line-height:1.5; color:var(--ef-sub); }
@media (prefers-reduced-motion: reduce) { .photo-upload__fill { transition:none; } }
.photo-upload__stage { display:flex; flex-direction:column; gap:14px; }
.photo-upload__back { align-self:flex-start; }
.photo-upload__result { gap:8px; cursor:default; }
.photo-upload__result:hover { transform:none; box-shadow:var(--ef-shadow-card); border-color:var(--ef-line); }
.photo-upload__result-title { margin:0; font-family:var(--ef-serif); font-weight:700; font-size:20px; color:var(--ef-ink); }
.photo-upload__result p { margin:0; font-size:14px; line-height:1.6; color:var(--ef-ink-soft); }
.photo-upload__done { align-self:flex-start; margin-top:4px; }
`;
  document.head.appendChild(style);
}
